import { gql } from '@apollo/client';
import { Box, Button, Divider, SxProps, Typography } from '@mui/material';
import { IconBoldHeart } from 'components/icons/components/bold/IconBoldHeart';
import { IconBoldShare } from 'components/icons/components/bold/IconBoldShare';
import { IconCustomUsers } from 'components/icons/components/custom/IconCustomUsers';
import {
  RelationshipBackgroundColorMap,
  RelationshipColorMap,
  RelationshipLabelMap,
  SentimentBackgroundColorMap,
  SentimentColorMap,
  SentimentLabelMap,
} from 'features/socialListeningCommunity';
import {
  CreatorProfileFragmentSlCreatorProfileDetailFragment,
  CreatorProfileFragmentSocialMediaListeningCreatorProfileFragment,
  CreatorProfileRelationshipStrength,
  Sentiment,
} from 'graphql/generated';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import { SocialMediaListeningCreatorProfile } from './SocialMediaListeningCreatorProfile';

// eslint-disable-next-line
gql`
  fragment CreatorProfileFragmentSLCreatorProfileDetail on CreatorProfileModel {
    id
    brandMentionCount
    totalComments
    averageSentimentScore
    relationshipStrength
  }
`;

type Props = {
  creator: CreatorProfileFragmentSocialMediaListeningCreatorProfileFragment;
  brandId?: string;
  creatorProfileStats?: CreatorProfileFragmentSlCreatorProfileDetailFragment;
  componentProps?: {
    renderCreatorTrackingButton?: () => React.ReactNode;
    renderBlockCreatorButton?: () => React.ReactNode;
    profileContainer?: {
      sx?: SxProps;
    };
  };
};

export const SocialMediaListeningPostCreatorProfileDetail = ({
  creator,
  brandId,
  componentProps,
  creatorProfileStats,
}: Props) => {
  const relationshipStrength =
    creatorProfileStats?.relationshipStrength ||
    CreatorProfileRelationshipStrength.Neutral;

  const sentiment = useMemo(() => {
    const sentimentScore = creatorProfileStats?.averageSentimentScore ?? 0;
    if (sentimentScore > 0) return Sentiment.Positive;
    if (sentimentScore < 0) return Sentiment.Negative;
    return Sentiment.Neutral;
  }, [creatorProfileStats]);

  const statsData = useMemo(
    () => [
      {
        label: 'Brand mentions',
        value: formatBigNumber(creatorProfileStats?.brandMentionCount ?? 0),
        backgroundColor: 'rgba(205, 226, 223, 0.50)',
        color: theme.colors?.utility['teal-4'],
      },
      {
        label: 'Comments',
        value: formatBigNumber(creatorProfileStats?.totalComments ?? 0),
        backgroundColor: 'rgba(230, 221, 237, 0.50)',
        color: theme.colors?.utility['purple-4'],
      },
      {
        label: 'Sentiment',
        value: SentimentLabelMap[sentiment],
        backgroundColor: SentimentBackgroundColorMap[sentiment],
        color: SentimentColorMap[sentiment],
      },
    ],
    [creatorProfileStats, sentiment],
  );

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{
          ...(componentProps?.profileContainer?.sx || {}),
        }}
      >
        <SocialMediaListeningCreatorProfile
          creator={creator}
          brandId={brandId}
          componentProps={{
            showStats: true,
            showDetailsOnHover: false,
            renderBlockCreatorButton: componentProps?.renderBlockCreatorButton,
            avatarSx: {
              sx: {
                width: theme.spacing(20),
                height: theme.spacing(20),
              },
            },
            containerSx: {
              sx: {
                color: theme.colors?.primary.black,
              },
            },
          }}
        />

        {creator.signature && (
          <Typography variant="headline-sm" fontWeight={500}>
            {creator.signature}
          </Typography>
        )}
      </Box>

      <Box display="flex" gap={2}>
        {creatorProfileStats && brandId && (
          <Link
            to={PlotRoutes.socialListeningCreatorProfile(
              brandId,
              creatorProfileStats.id,
            )}
          >
            <Button
              variant="primary-alt"
              startIcon={
                <IconBoldHeart size={16} color={theme.colors?.primary.black} />
              }
              sx={{
                borderRadius: 2,
                backgroundColor: 'transparent',
                border: `2px solid ${theme.colors?.primary.black}`,
                color: theme.colors?.primary.black,
                ...theme.typography['headline-sm'],
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              See community profile
            </Button>
          </Link>
        )}

        {componentProps?.renderCreatorTrackingButton &&
          componentProps?.renderCreatorTrackingButton()}
      </Box>

      <Divider sx={{ borderColor: 'rgba(35, 6, 3, 0.07)' }} />
      {creatorProfileStats && (
        <>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ w: theme.spacing(4) }}>
              <IconBoldHeart size={16} color={theme.colors?.utility[700]} />
            </Box>
            <Box display="flex" flexDirection="column" gap={3} width="100%">
              <Typography
                variant="headline-xs"
                color={theme.colors?.utility[700]}
              >
                Your Community
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 4,
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                {statsData.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3.5,
                      p: 3,
                      borderRadius: 4,
                      backgroundColor: stat.backgroundColor,
                      flex: 1,
                    }}
                  >
                    <Typography
                      variant="headline-xs"
                      color={theme.colors?.utility[700]}
                      whiteSpace="nowrap"
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="headline-xl"
                      fontWeight={500}
                      fontSize={28}
                      color={stat.color}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <Box sx={{ w: theme.spacing(4) }}>
              <IconBoldShare size={16} color={theme.colors?.utility[700]} />
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography
                variant="headline-xs"
                color={theme.colors?.utility[700]}
              >
                Relationship
              </Typography>
              <Box
                sx={{
                  background:
                    RelationshipBackgroundColorMap[relationshipStrength],
                  borderRadius: 2,
                  p: theme.spacing(1, 3),
                  width: 'fit-content',
                }}
              >
                <Typography
                  variant="headline-sm"
                  color={RelationshipColorMap[relationshipStrength]}
                >
                  {RelationshipLabelMap[relationshipStrength]}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
      {creator.audience_demographics && (
        <Box display="flex" color={theme.colors?.utility[700]} gap={2}>
          <Box sx={{ w: theme.spacing(4) }}>
            <IconCustomUsers size={16} />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="body-lg" fontWeight={600}>
              Audience demographic
            </Typography>
            <Typography
              variant="body-xl"
              fontWeight={500}
              color={theme.colors?.primary.black}
            >
              {creator.audience_demographics}
            </Typography>
          </Box>
        </Box>
      )}
      {creator.industry && (
        <Box display="flex" color={theme.colors?.utility[700]} gap={2}>
          <Box sx={{ w: theme.spacing(4) }}>
            <IconCustomUsers size={16} />
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="body-lg" fontWeight={600}>
              Niche
            </Typography>
            <Typography
              variant="body-xl"
              fontWeight={500}
              color={theme.colors?.primary.black}
            >
              {creator.industry.split('|').join(', ')}
            </Typography>
          </Box>
        </Box>
      )}
      {/* TODO: To be done later */}
      {/* <Box display="flex" color={theme.colors?.utility[700]} gap={2}>
            <Box sx={{ w: theme.spacing(4) }}>
              <IconCustomUsers size={16} />
            </Box>
            <Box display="flex" flexDirection="column">
              <Typography variant="body-lg" fontWeight={600}>
                Other insights
              </Typography>
              <Typography
                variant="body-xl"
                fontWeight={500}
                color={theme.colors?.primary.black}
              >
                The creator engages in activities like pottery and fashion,
                suggesting content that combines personal storytelling with visual
                creativity. The inclusion of themes like pregnancy and relationships
                indicates that it is relatable and personal, likely appealing to an
                audience in genuine life updates and experiences
              </Typography>
            </Box>
          </Box> */}
    </>
  );
};
